import React, { useContext, useState } from "react";
import "./DataSourceDefInstanceActions.scss";
import { useTranslation } from "react-i18next";
import {
  CNC_CHECK_DATASOURCE_CONNECTION,
  CNC_LAUNCH_COLLECTION_ACTION,
  type CncCheckDatasourceConnectionData,
  type CncLaunchCollectionActionData,
} from "../../../CustomerProvisioningApi";
import type { FetchResult } from "@apollo/client";
import { showToast } from "../../../../Forms/Dialogs/Toast/Toast";
import DataSourceInstanceDeleteDatasourceData from "./DataSourceInstanceDeleteDatasourceData";
import {
  IconButton,
  useMutationWrapper,
  ModalContext,
  showErrorDialog,
} from "@SeemplicityDev/shared";

export interface DataSourceDefInstanceActionsProps {
  accountUuid: string;
  accountName: string;
  dsInstanceID: string;
  dsInstanceName: string;
  enableCheckConnection: boolean;
}

const DataSourceDefInstanceActions: React.FC<
  DataSourceDefInstanceActionsProps
> = ({
  accountUuid,
  accountName,
  dsInstanceID,
  dsInstanceName,
  enableCheckConnection,
}) => {
  const { t } = useTranslation();
  const [checkDatasourceConnection] =
    useMutationWrapper<CncCheckDatasourceConnectionData>(
      CNC_CHECK_DATASOURCE_CONNECTION
    );
  const { openModal } = useContext(ModalContext);

  const [launchCollectionAction] =
    useMutationWrapper<CncLaunchCollectionActionData>(
      CNC_LAUNCH_COLLECTION_ACTION
    );

  const [enableCollectNowBtn, setEnableCollectNowBtn] = useState(true);
  const [enableCheckConnectionBtn, setEnableCheckConnectionBtn] =
    useState(true);

  const checkConnection = () => {
    setEnableCheckConnectionBtn(false);
    checkDatasourceConnection({
      variables: {
        account_uuid: accountUuid,
        datasource_id: dsInstanceID,
      },
    })
      .then(() => {
        showToast({
          message: t("Checking connection..."),
          description: accountName,
        });
      })
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      })
      .finally(() => {
        setEnableCheckConnectionBtn(true);
      });
  };

  const launchCollection = () => {
    setEnableCollectNowBtn(false);
    launchCollectionAction({
      variables: { account_uuid: accountUuid, subject_id: dsInstanceID },
    })
      .then((res: FetchResult<CncLaunchCollectionActionData>) => {
        showToast({ message: res?.data?.cnc_launch_collection_action?.msg });
      })
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      })
      .finally(() => {
        setEnableCollectNowBtn(true);
      });
  };

  const deleteDatasourceData = () => {
    openModal({
      title: "Delete Datasource Data",
      size: "s",
      overlay: true,
      component: (
        <DataSourceInstanceDeleteDatasourceData
          accountUuid={accountUuid}
          dsInstanceID={dsInstanceID}
          dsInstanceName={dsInstanceName}
        />
      ),
    });
  };

  return (
    <div className={"ds-instance-action-container form-button-group"}>
      {enableCheckConnection && (
        <IconButton
          wrapperClassName={"check-connection-btn"}
          data-testid={"check-connection"}
          withTooltip={{ content: t("Check Connection") }}
          isIcon={
            enableCheckConnectionBtn
              ? "seem-icon-connection"
              : "seem-icon-spinner spinner"
          }
          type={`button`}
          onClick={checkConnection}
          disabled={!enableCheckConnectionBtn}
        />
      )}
      <IconButton
        wrapperClassName={"collect-now-btn"}
        data-testid={"collect-now"}
        withTooltip={{ content: t("Collect Now") }}
        isIcon={
          enableCollectNowBtn
            ? "seem-icon-collect"
            : "seem-icon-spinner spinner"
        }
        type={`button`}
        onClick={launchCollection}
        disabled={!enableCollectNowBtn}
      />
      <IconButton
        wrapperClassName={"delete-datasource-data-btn"}
        buttonStyle={"danger"}
        data-testid={"delete-datasource-data"}
        withTooltip={{ content: t("Delete Datasource Data") }}
        isIcon={"seem-icon-bin"}
        type={`button`}
        onClick={deleteDatasourceData}
      />
    </div>
  );
};
export default DataSourceDefInstanceActions;
